import { createAudioEngine, NOOP_ENGINE, type AmbientMood, type AudioEngine, type SfxId } from '@strategy4kids/audio';
import { effectiveAudio, type AudioSettingsData, useAudioSettingsStore } from '../state/audio-settings-store.js';

/**
 * The app's one audio engine. The spec (docs/proposals/ai-personas-voice-and-sound.md §P1) wants a
 * single place that owns the context, the unlock gesture and the settings sync, so stores and
 * screens just call `playSfx('chess-slide')` and never touch Web Audio.
 *
 * The engine is the no-op until the first real user gesture unlocks a context, and it is the no-op
 * under `?engine=fake` (e2e and the audit runner must be silent). Music defaults to OFF (ratified);
 * ambience only ever starts when the store says so.
 */

function engineForEnvironment(): AudioEngine {
  if (typeof window === 'undefined') return NOOP_ENGINE;
  const params = new URLSearchParams(window.location.search);
  if (params.get('engine') === 'fake') return NOOP_ENGINE;
  return createAudioEngine({ createContext: () => new AudioContext() });
}

const engine = engineForEnvironment();

/**
 * Push the settings into the mixer.
 *
 * It takes the EFFECTIVE settings, never the raw ones: a grown-up's lock and the volume cap are
 * enforced here, so a locked setting cannot be changed from the game even by a code path that
 * forgets to check the lock. The cap rides on the master bus, which the mixer multiplies into
 * every other bus — so "half as loud" really is half as loud for every sound.
 */
export function applyAudioSettings(target: AudioEngine, settings: AudioSettingsData): void {
  const eff = effectiveAudio(settings);
  target.setMuted(eff.muted);
  target.setVolumes({ master: eff.master, sfx: eff.sfx, music: eff.music, voice: eff.voice });
  target.setSensoryFriendly(eff.sensory);
  if (eff.music <= 0 || eff.sensory) target.stopAmbient();
}

function syncFromStore(): void {
  applyAudioSettings(engine, useAudioSettingsStore.getState());
}

/** Called once from `src/index.ts`: install the gesture unlock + visibility listeners. */
export function initAudio(): void {
  if (typeof window === 'undefined') return;
  syncFromStore();
  useAudioSettingsStore.subscribe(syncFromStore);
  const unlock = () => {
    engine.unlock();
    const eff = effectiveAudio(useAudioSettingsStore.getState());
    if (eff.music > 0 && !eff.sensory) engine.startAmbient(currentMood());
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock, { passive: true });
  document.addEventListener('visibilitychange', () => {
    engine.handleVisibility(document.visibilityState === 'visible');
  });
}

/** The mood for the current screen family; the router is not reachable from here, so callers set it. */
let mood: AmbientMood = 'menu';
export function setAmbientMood(next: AmbientMood): void {
  mood = next;
  const eff = effectiveAudio(useAudioSettingsStore.getState());
  if (eff.music > 0 && !eff.sensory) engine.startAmbient(next);
}
function currentMood(): AmbientMood {
  return mood;
}

export function playSfx(id: SfxId, pan?: number): void {
  engine.play(id, pan);
}
